/**
 * OL Header — assets/ol-header.js
 * Click mega menu + sticky/scroll behavior + mobile drawer
 */

(function () {
  'use strict';

  // ── Desktop Mega menu ──────────────────────────────────────────────────────
  class OLMegaMenu {
    constructor(wrapper) {
      this.wrapper = wrapper;
      this.items = Array.from(wrapper.querySelectorAll('.ol-header__nav-item--has-mega'));
      this.init();
    }

    init() {
      this.items.forEach(item => {
        const link = item.querySelector('.ol-header__nav-link');
        const megaId = item.dataset.megaId;
        const megaPanel = megaId ? document.getElementById(megaId) : null;

        if (link) {
          link.addEventListener('click', (e) => {
            e.preventDefault();
            const isOpen = item.classList.contains('ol-mega-open');
            this.closeAll();
            if (!isOpen) this.openItem(item, megaPanel, link);
          });
        }

        item.addEventListener('keydown', e => {
          if (e.key === 'Escape') {
            this.closeAll();
            if (link) link.focus();
          }
        });
      });

      document.addEventListener('click', e => {
        // Close if click is outside wrapper AND outside any open mega panel
        const clickedInsideWrapper = this.wrapper.contains(e.target);
        const clickedInsideMega = e.target.closest('.ol-mega');
        if (!clickedInsideWrapper && !clickedInsideMega) {
          this.closeAll();
        }
      });
    }

    openItem(item, megaPanel, link) {
      item.classList.add('ol-mega-open');
      if (link) link.setAttribute('aria-expanded', 'true');
      if (megaPanel) {
        megaPanel.classList.add('ol-mega--open');
        megaPanel.removeAttribute('aria-hidden');
      }
    }

    closeItem(item) {
      item.classList.remove('ol-mega-open');
      const link = item.querySelector('.ol-header__nav-link');
      if (link) link.setAttribute('aria-expanded', 'false');

      const megaId = item.dataset.megaId;
      if (megaId) {
        const megaPanel = document.getElementById(megaId);
        if (megaPanel) {
          megaPanel.classList.remove('ol-mega--open');
          megaPanel.setAttribute('aria-hidden', 'true');
        }
      }
    }

    closeAll() {
      this.items.forEach(item => this.closeItem(item));
    }
  }

  // ── Mobile nav drawer ──────────────────────────────────────────────────────
  class OLMobileNav {
    constructor() {
      this.drawer   = document.getElementById('ol-mobile-nav');
      this.overlay  = document.querySelector('.ol-mobile-nav__overlay');
      this.hamburger = document.querySelector('.ol-header__hamburger');
      this.closeBtn  = document.querySelector('.ol-mobile-nav__close');
      if (!this.drawer) return;
      this.init();
    }

    init() {
      if (this.hamburger) {
        this.hamburger.addEventListener('click', () => this.open());
      }

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      document.addEventListener('keydown', e => {
        if (e.key === 'Escape') this.close();
      });

      // Mobile accordion triggers
      const triggers = this.drawer.querySelectorAll('.ol-mobile-nav__trigger');
      triggers.forEach(trigger => {
        trigger.addEventListener('click', () => {
          const sub = trigger.nextElementSibling;
          const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

          // Close all other open subs
          triggers.forEach(t => {
            if (t !== trigger) {
              t.setAttribute('aria-expanded', 'false');
              const s = t.nextElementSibling;
              if (s) s.hidden = true;
            }
          });

          trigger.setAttribute('aria-expanded', String(!isExpanded));
          if (sub) sub.hidden = isExpanded;
        });
      });
    }

    open() {
      this.drawer.classList.add('ol-mobile-nav--open');
      this.drawer.removeAttribute('aria-hidden');
      if (this.overlay) this.overlay.classList.add('ol-mobile-nav__overlay--visible');
      if (this.hamburger) this.hamburger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.drawer.classList.remove('ol-mobile-nav--open');
      this.drawer.setAttribute('aria-hidden', 'true');
      if (this.overlay) this.overlay.classList.remove('ol-mobile-nav__overlay--visible');
      if (this.hamburger) this.hamburger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
  }

  // ── Sticky header ──────────────────────────────────────────────────────────
  class OLStickyHeader {
    constructor(wrapper) {
      this.wrapper    = wrapper;
      this.stickyType = wrapper.dataset.sticky;
      this.lastY      = window.scrollY;
      this.ticking    = false;
      this.init();
    }

    init() {
      if (this.stickyType === 'none') return;

      this.setHeightVar();
      window.addEventListener('resize', () => this.setHeightVar(), { passive: true });

      if (this.stickyType === 'on-scroll-up') {
        window.addEventListener('scroll', () => this.tickScroll(), { passive: true });
      }

      if (this.stickyType === 'reduce-logo-size') {
        window.addEventListener('scroll', () => this.tickReduce(), { passive: true });
      }
    }

    setHeightVar() {
      // Announcement bar is pinned above the fixed header; the header's
      // `top` and the body's padding-top both key off this value.
      const slot = document.querySelector('.ol-announcement-slot');
      const slotHeight = slot ? slot.offsetHeight : 0;
      document.documentElement.style.setProperty(
        '--ol-announcement-height',
        `${slotHeight}px`
      );
      document.documentElement.style.setProperty(
        '--ol-header-height',
        `${this.wrapper.offsetHeight}px`
      );
    }

    tickScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const h = this.wrapper.offsetHeight;
        if (y > this.lastY && y > h) {
          this.wrapper.classList.add('ol-header--hidden');
        } else if (y < this.lastY) {
          this.wrapper.classList.remove('ol-header--hidden');
        }
        this.lastY   = y;
        this.ticking = false;
      });
    }
 
    tickReduce() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        if (window.scrollY > 50) {
          this.wrapper.classList.add('ol-header--scrolled');
        } else {
          this.wrapper.classList.remove('ol-header--scrolled');
        }
        this.ticking = false;
      });
    }
  }

  // ── Boot ───────────────────────────────────────────────────────────────────
  function init() {
    const wrapper = document.querySelector('.ol-header-wrapper');
    if (!wrapper) return;
    new OLMegaMenu(wrapper);
    new OLMobileNav();
    new OLStickyHeader(wrapper);
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();

})();